import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { Avatar } from '@/components/Chips'
import { Icon } from '@/components/Icon'
import { PageHeader, StatTiles } from '@/components/PageHeader'
import {
  fetchAccessReview,
  revokeMembership,
  type AccessReview,
  type AccessReviewRow,
} from '@/features/access/accessReview'
import type { ApiError } from '@/lib/api/client'

/**
 * Who can reach an AWJ project from outside, and how long they still can.
 *
 * "The report nobody remembers to run manually" (organization.md). Ordered by
 * the server with the worst first — no expiry, then already lapsed, then
 * soonest — so a review read top-down meets its problems before its noise.
 *
 * Every row can be revoked from here. A report you cannot act on is a report
 * nobody opens twice.
 */
export function AccessReviewPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [onlyFindings, setOnlyFindings] = useState(false)

  const review = useQuery<AccessReview, ApiError>({
    queryKey: ['access-review'],
    queryFn: fetchAccessReview,
    retry: false,
  })

  const revoke = useMutation<void, ApiError, string>({
    mutationFn: (id) => revokeMembership(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['access-review'] }),
  })

  if (review.isPending) {
    return <p className="text-ink-500 font-mono text-xs">{t('common.loading')}</p>
  }

  // The endpoint answers 404 to anyone who may not review, so there is nothing
  // to explain here beyond the page not existing for them.
  if (review.isError) {
    return <h1 className="text-ink-900 text-xl font-semibold">{t('project.notFound')}</h1>
  }

  const rows = onlyFindings
    ? review.data.rows.filter((row) => row.findings.length > 0)
    : review.data.rows

  return (
    <div>
      <PageHeader title={t('access.reviewTitle')} subtitle={t('access.reviewSubtitle')} />

      <StatTiles
        items={[
          { label: t('access.guests'), value: review.data.total },
          { label: t('access.needAttention'), value: review.data.needAttention },
        ]}
      />

      <div className="mb-3 flex items-center gap-0.5">
        {([false, true] as const).map((only) => (
          <button
            key={String(only)}
            type="button"
            onClick={() => setOnlyFindings(only)}
            aria-pressed={onlyFindings === only}
            className={`rounded px-2.5 py-1 text-[12px] ${
              onlyFindings === only
                ? 'bg-surface-2 text-ink-900 font-medium'
                : 'text-ink-500 hover:text-ink-900'
            }`}
          >
            {only ? t('access.onlyFindings') : t('access.allRows')}
          </button>
        ))}
      </div>

      {revoke.isError ? (
        <p className="mb-3 text-[12px] text-[color:var(--color-state-blocked)]">
          {revoke.error.message}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
          {t('access.noGuests')}
        </p>
      ) : (
        <div className="border-line bg-surface overflow-x-auto rounded-md border">
          <table className="w-full min-w-[860px] text-sm">
            <thead>
              <tr className="border-line-strong text-ink-500 border-b font-mono text-[10px] tracking-widest uppercase">
                <th scope="col" className="px-4 py-2.5 text-start font-medium">{t('access.guest')}</th>
                <th scope="col" className="px-4 py-2.5 text-start font-medium">{t('access.client')}</th>
                <th scope="col" className="px-4 py-2.5 text-start font-medium">{t('access.project')}</th>
                <th scope="col" className="px-4 py-2.5 text-start font-medium">{t('access.invitedBy')}</th>
                <th scope="col" className="px-4 py-2.5 text-end font-medium">{t('access.expires')}</th>
                <th scope="col" className="px-4 py-2.5" />
              </tr>
            </thead>

            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  pending={revoke.isPending}
                  onRevoke={() => {
                    if (window.confirm(t('access.revokeConfirm'))) revoke.mutate(row.id)
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Row({
  row,
  pending,
  onRevoke,
}: {
  row: AccessReviewRow
  pending: boolean
  onRevoke: () => void
}) {
  const { t } = useTranslation()

  return (
    <tr className="border-line border-b align-top last:border-0">
      <td className="px-4 py-2.5">
        <span className="flex items-center gap-2">
          <Avatar name={row.guest?.name ?? null} size="sm" />
          <span className="min-w-0">
            <span className="text-ink-900 block truncate text-[13px]">{row.guest?.name ?? '—'}</span>
            <span className="text-ink-500 block truncate font-mono text-[11px]">
              {row.guest?.email ?? ''}
            </span>
          </span>
        </span>

        {row.findings.length === 0 ? null : (
          <ul className="mt-1.5 flex flex-col gap-0.5">
            {row.findings.map((finding) => (
              <li
                key={finding}
                className="flex items-center gap-1 text-[11px] text-[color:var(--color-state-blocked)]"
              >
                <Icon name="flag" className="size-3 shrink-0" />
                {t(`access.finding.${finding}`)}
              </li>
            ))}
          </ul>
        )}
      </td>

      <td className="text-ink-700 px-4 py-2.5 text-[13px]">{row.client?.name ?? '—'}</td>

      <td className="px-4 py-2.5">
        {row.project === null ? (
          <span className="text-ink-500">—</span>
        ) : (
          <Link to={`/projects/${row.project.key}`} className="text-brand-600 font-mono text-[11px]">
            {row.project.key}
          </Link>
        )}
      </td>

      <td className="text-ink-700 px-4 py-2.5 text-[13px]">{row.invitedBy ?? '—'}</td>

      <td className="px-4 py-2.5 text-end">
        <Countdown row={row} />
      </td>

      <td className="px-4 py-2.5 text-end">
        <button
          type="button"
          onClick={onRevoke}
          disabled={pending}
          className="text-ink-500 hover:text-[color:var(--color-state-blocked)] text-[12px] underline disabled:opacity-40"
        >
          {t('access.revoke')}
        </button>
      </td>
    </tr>
  )
}

/**
 * The live countdown the definition of done asks for.
 *
 * The number of days comes from the server, which computed it against one
 * instant — so two rows an inch apart cannot disagree about what "today" is.
 */
function Countdown({ row }: { row: AccessReviewRow }) {
  const { t } = useTranslation()

  if (row.daysRemaining === null) {
    return (
      <span className="font-mono text-[11px] text-[color:var(--color-state-blocked)]">
        {t('access.noExpiry')}
      </span>
    )
  }

  const lapsed = row.daysRemaining < 0
  const soon = row.daysRemaining >= 0 && row.daysRemaining <= 7

  return (
    <span className="flex flex-col items-end gap-0.5">
      <span
        className={`font-mono text-[11px] tabular-nums ${
          lapsed || soon ? 'font-semibold text-[color:var(--color-state-blocked)]' : 'text-ink-700'
        }`}
      >
        {row.daysRemaining === 0
          ? t('access.today')
          : lapsed
            ? t('access.lapsedDays', { count: Math.abs(row.daysRemaining) })
            : t('access.inDays', { count: row.daysRemaining })}
      </span>
      <span className="text-ink-500 font-mono text-[10px]">{row.expiresAt?.slice(0, 10)}</span>
    </span>
  )
}
